import React from 'react';
import { WithTranslation, withTranslation } from 'react-i18next';
import styled from 'styled-components/macro';

import { colors, distances, border } from '../../styles/constants';
import Card from '../../components/Card';
import { H3 } from '../../components/Typography';
import { DraftOrder, Order } from '../../types/orders';
import { Dl, Dt, Dd } from '../../components/DescriptionList/Horizontal';
import { DateComponent } from '../../components/Formatters';
import { getCustomerType, getGender } from '../../customers/customerHelpers';
import { Customer } from '../../types/customers';
import ExpandCollapseJson from './ExpandCollapseJson';
import Hr from '../../components/Hr';

export interface DraftOrderCustomerProps extends WithTranslation {
    draftOrOrder: DraftOrder | Order;
}

const getCustomer = (draftOrOrder: DraftOrder | Order) => {
    if ((draftOrOrder as DraftOrder).order) {
        return (draftOrOrder as DraftOrder).order.customer;
    }
    if ((draftOrOrder as Order).items) {
        return (draftOrOrder as Order).customer;
    }
    return undefined;
};

const CustomerView = (
    {
        t,
        draftOrOrder,
    }: DraftOrderCustomerProps
) => {
    const customer = getCustomer(draftOrOrder);

    if (!customer) {
        return null;
    }
    return (
        <Card marginBottom={distances.normal}>
            <H3>{t('orders.fields.customer.header')}</H3>
            <Hr />

            {customer.id && (
                <Dl>
                    <Dt doubleColumn>{t('orders.fields.customer.id')}</Dt>
                    <Dd>{customer.id}</Dd>
                </Dl>
            )}
            {customer.first_name && (
                <Dl>
                    <Dt doubleColumn>{t('orders.fields.customer.first_name')}</Dt>
                    <Dd>{customer.first_name}</Dd>
                </Dl>
            )}
            {customer.last_name && (
                <Dl>
                    <Dt doubleColumn>{t('orders.fields.customer.last_name')}</Dt>
                    <Dd>{customer.last_name}</Dd>
                </Dl>
            )}
            {customer.gender && (
                <Dl>
                    <Dt doubleColumn>{t('orders.fields.customer.gender')}</Dt>
                    <Dd>{getGender(customer as Customer)}</Dd>
                </Dl>
            )}
            {customer.email && (
                <Dl>
                    <Dt doubleColumn>{t('orders.fields.customer.email')}</Dt>
                    <Dd>{customer.email}</Dd>
                </Dl>
            )}
            {customer.phone_number && (
                <Dl>
                    <Dt doubleColumn>{t('orders.fields.customer.phone_number')}</Dt>
                    <Dd>{customer.phone_number}</Dd>
                </Dl>
            )}
            {customer.pays_tax && (
                <Dl>
                    <Dt doubleColumn>{t('orders.fields.customer.pays_tax')}</Dt>
                    <Dd>{t(`orders.fields.customer.pays_tax_values.${customer.pays_tax}`)}</Dd>
                </Dl>
            )}
            {customer.date_of_birth && (
                <Dl>
                    <Dt doubleColumn>{t('orders.fields.customer.date_of_birth')}</Dt>
                    <Dd>
                        <DateComponent isoString={customer.date_of_birth} />
                    </Dd>
                </Dl>
            )}
            {customer.type && (
                <Dl>
                    <Dt doubleColumn>{t('orders.fields.customer.type')}</Dt>
                    <Dd>{getCustomerType(customer as Customer)}</Dd>
                </Dl>
            )}
            {customer.enrolled_by && (
                <Dl>
                    <Dt doubleColumn>{t('orders.fields.customer.enrolled_by')}</Dt>
                    <Dd>{customer.enrolled_by.value || 'N/A'}</Dd>
                </Dl>
            )}
            {customer.company && (
                <Dl>
                    <Dt doubleColumn>{t('orders.fields.customer.company.business_name')}</Dt>
                    <Dd>{customer.company.bussiness_name || 'N/A'}</Dd>
                </Dl>
            )}
            {customer.metadata && (
                <ExpandCollapseJson
                    label={<strong>{t('orders.fields.click_to_expand_metadata')}</strong>}
                    json={customer.metadata}
                />
            )}
        </Card>
    );
};

export default withTranslation()(CustomerView);
